import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '../ui/sheet';
import { User, LogOut, Settings, Shield, Menu, LayoutDashboard } from 'lucide-react';
import AutoxiumLogo from '../AutoxiumLogo';
import NavbarSearch from './NavbarSearch';

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // The profile menu is non-modal so the page stays scrollable; close it as
  // soon as the user scrolls rather than leaving it floating over content.
  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    const close = () => setProfileMenuOpen(false);
    window.addEventListener('scroll', close, { passive: true });
    return () => window.removeEventListener('scroll', close);
  }, [profileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home', active: true },
    { path: '/articles', label: 'Articles', active: true },
    { path: '/how-it-works', label: 'How Does It Work?', active: true },
    { path: '/simulation', label: 'Simulation', active: true },
    { path: '/tools', label: 'Tools', active: true },
    { path: '/courses', label: 'Courses', comingSoon: true },
    { path: '/community', label: 'Community', active: true },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navTestId = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <header className="sticky top-0 z-50 bg-chassis text-white" data-testid="main-header">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="header-logo"
            aria-label="AUTOXIUM home"
          >
            {/* Mark only on phones so the header row fits; full lockup from sm up */}
            <AutoxiumLogo variant="light" width={120} height={32} wordmarkClassName="hidden sm:block" />
          </Link>

          {/* Navigation */}
          <nav className="hidden xl:flex items-center gap-1" data-testid="main-navigation">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative whitespace-nowrap px-4 py-2 font-medium text-sm uppercase tracking-wider transition-colors ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid={`nav-${navTestId(item.label)}`}
              >
                {item.label}
                {item.comingSoon && (
                  <span className="absolute -top-1 -right-1 text-[8px] font-mono bg-autoxium-blue px-1 rounded">
                    SOON
                  </span>
                )}
                {isActive(item.path) && (
                  <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-autoxium-blue" />
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side - Auth */}
          <div className="relative flex items-center gap-1 sm:gap-3">
            <NavbarSearch />

            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-white/80 hover:text-white hover:bg-white/10"
                      data-testid="admin-dashboard-btn"
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">Admin</span>
                    </Button>
                  </Link>
                )}
                <DropdownMenu modal={false} open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="rounded-full bg-white/10 hover:bg-white/20"
                      data-testid="profile-menu-trigger"
                    >
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-2 border-b border-border">
                      <p className="font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer" data-testid="dashboard-menu-item">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="profile-menu-item">
                        <Settings className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="cursor-pointer text-destructive"
                      data-testid="logout-menu-item"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-white/80 hover:text-white hover:bg-white/10"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm" 
                    className="bg-autoxium-blue hover:bg-autoxium-blue/90 text-white font-bold uppercase tracking-wider"
                    data-testid="register-btn"
                  >
                    Register
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Navigation Trigger */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="xl:hidden text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="mobile-nav-trigger"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-chassis text-white border-white/10 w-4/5">
                <nav className="flex flex-col gap-1 mt-8" data-testid="mobile-navigation">
                  {navItems.map((item) => (
                    <SheetClose asChild key={item.path}>
                      <Link
                        to={item.path}
                        className={`relative px-4 py-3 rounded-md font-medium text-sm uppercase tracking-wider ${
                          isActive(item.path)
                            ? 'text-white bg-white/10 border-l-2 border-autoxium-blue'
                            : 'text-white/60 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {item.label}
                        {item.comingSoon && (
                          <span className="ml-2 text-[8px] font-mono bg-autoxium-blue px-1 rounded align-middle">
                            SOON
                          </span>
                        )}
                      </Link>
                    </SheetClose>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
